import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Birdseed } from "@coverme/elements";
import { MiniFacesheetContainer } from "../components/MiniFacesheet/MiniFacesheet";
import * as programActions from "../actions/programActions";
import * as documentActions from "../actions/documentActions";
import { LoadingBar } from "../components/LoadingComponents/LoadingBar";
import { MarketplaceCards } from "../components/MarketplaceCards/MarketplaceCards";
import { some } from "lodash-es";
import { HospitalCard } from "@coverme/cards";
import { getNextUrl } from "../services/marketplaceWorkflowService";
import { getNavigationUrlByEncounterId } from "../services/encounterNavigationService";
import * as tagsAction from "../actions/tagsActions";

const MarketplacePostApproval = ({
    interview,
    encounterId,
    household,
    notes,
    person,
    programs,
    customer,
    documents,
    approveProgram,
    denyProgram,
    history,
    shouldShowUmmcNavigation,
    getEncounterTag,
}) => {
    let interviewPersonId = interview?.entry?.patient?.personId;

    useEffect(() => {
        getEncounterTag(encounterId);
        const selectedProgram = programs.data.filter(
            x => x.eligibilityDeterminationStatus.name === "Pending Decision"
        )[0];

        if (selectedProgram == null) {
            return;
        }

        if (selectedProgram.requestingUserCanApprove === false) {
            return history.replace(`/interview/${encounterId}/details/dispatch`);
        }
    }, [programs]);

    const program = useMemo(() => {
        return programs.data.filter(x => x.eligibilityDeterminationStatus.name === "Pending Decision")[0];
    }, [programs]);

    const eligibleFormattedPrograms = useMemo(() => {
        if (program == null) {
            return [];
        }

        return [program].map((x, i) => ({
            name: x.programId,
            isBestFit: false,
            predictedEligibilityStatus: "APPROVED",
            programName: x.name,
            programDetails: x.details,
            offerId: x.offerId,
            requestingUserCanApprove: x.requestingUserCanApprove,
            eligibilityDeterminationResultId: x.eligibilityDeterminationResultId,
            canCardBeDenied: x.canCardBeDenied,
            cardTemplate: getCardTemplate(x),
            supplementalInfoTemplate: x.supplementalInfoTemplate,
            requiresPostSelectionApproval: x.requiresPostSelectionApproval,
        }));
    }, [program]);

    function onApprove() {
        approveProgram(program, customer?.data?.customer?.GlobalId).then(() => {
            getEncounterTag(encounterId);
            const url = getNextUrl(encounterId, interview, program);
            history.push(url);
        });
    }

    function onDeny() {
        denyProgram(program, customer?.data?.customer?.GlobalId).then(() => {
            getEncounterTag(encounterId);
            history.push(`/interview/${encounterId}/details/marketplace`);
        });
    }

    return (
        <>
            <Birdseed>
                <Link to={"/"}>Home</Link>
                <a href="javascript:void(0)">
                    Interview / Marketplace / <strong>Approval</strong>
                </a>
            </Birdseed>
            <div className="post-marketplace-container">
                <MiniFacesheetContainer />
                {eligibleFormattedPrograms[0]?.requestingUserCanApprove === true && (
                    <LoadingBar isLoading={program == null || eligibleFormattedPrograms?.length === 0}>
                        <div>
                            <h2 className="title">Program Approval</h2>
                            <p>
                                <a href={`/interview/${encounterId}/details/facesheet`}>Click here</a> to open the
                                facesheet to review all the information about the case. <br />
                                You can then either approve or deny the recommendation.
                            </p>
                            {shouldShowUmmcNavigation && (
                                <p>
                                    <Link
                                        to={`/interview/${encounterId}/details/UMMCFADocumentation`}
                                        className="fa_documentation_link"
                                    >
                                        {"Navigate to FA Documentation"}
                                    </Link>
                                </p>
                            )}
                            <HospitalCard
                                program={eligibleFormattedPrograms[0]}
                                detailsEnabled={false}
                                detailsOpen={false}
                                onApprove={onApprove}
                                onDeny={onDeny}
                                canDeny={true}
                                canApprove={eligibleFormattedPrograms[0]?.requestingUserCanApprove}
                            />
                        </div>
                    </LoadingBar>
                )}
            </div>
        </>
    );
};

function getCardTemplate(program) {
    switch (program.assistanceCategory) {
        case "Banking Partner":
            return "CURAE";
        default:
            return "HOSPITAL";
    }
}

export const MarketplacePostApprovalContainer = connect(
    (state, ownProps) => {
        const patientId = state.interview?.entry?.patient?.personId;
        let underlyingPerson = null;
        if (patientId) {
            underlyingPerson = state.person.data[patientId];
        }
        return {
            interview: state.interview,
            encounterId: ownProps.match.params.encounterId,
            household: state.household,
            notes: state.notes.data[(underlyingPerson?.globalId)],
            person: underlyingPerson,
            documents: state.documents,
            programs: state.programs,
            customer: state.customer,
            shouldShowUmmcNavigation: state.interviewLookups.data?.configuration?.showDocumentationUMMCFA,
        };
    },
    dispatch => ({
        approveProgram: (program, customerId) => dispatch(programActions.approveProgram(program, customerId)),
        denyProgram: (program, customerId) => dispatch(programActions.denyProgram(program, customerId)),
        getEncounterTag: encounterId => dispatch(tagsAction.getEncounterTag(encounterId)),
    })
)(MarketplacePostApproval);
